import { Panel, PanelLabel } from "@/components/PageHeader";
import type { CardPayment, Occurrence, ScheduledResponse } from "@/lib/scheduled";
import { formatDate, money } from "@/lib/format";

const WEEK = ["L", "M", "M", "J", "V", "S", "D"];

export function StatCards({ s }: { s: ScheduledResponse["summary"] }) {
  const main = s.main_account;
  const cards: [string, string, string, string][] = [
    ["Por entrar", money(s.to_receive), "text-[#1B6E4F]", "stat-to-receive"],
    ["Por salir", money(s.to_pay), "text-[#1B2A4A]", "stat-to-pay"],
    [main ? `Quedaría en ${main.name}` : "Cuenta principal", main ? money(main.after) : "—", main && main.after < 0 ? "text-[#B23A1E]" : "text-[#1B2A4A]", "stat-main-after"],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {cards.map(([l, v, c, id]) => (
        <Panel key={id} testId={id}>
          <PanelLabel>{l}</PanelLabel>
          <p className={`tabular mt-2 font-display text-2xl font-extrabold sm:text-3xl ${c}`} data-testid={`${id}-value`}>{v}</p>
          {id === "stat-main-after" && main && <p className="tabular mt-1 text-xs text-slate-500">Hoy tiene {money(main.balance)}</p>}
        </Panel>
      ))}
    </div>
  );
}

export function MonthCalendar({ month, today, items }: { month: string; today: string; items: Occurrence[] }) {
  const [y, m] = month.split("-").map(Number);
  const days = new Date(y, m, 0).getDate();
  const offset = (new Date(y, m - 1, 1).getDay() + 6) % 7;
  const marked = new Set(items.map((o) => Number(o.date.slice(8, 10))));
  const cells = [...Array(offset).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <Panel testId="scheduled-calendar">
      <PanelLabel>Calendario del mes</PanelLabel>
      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-xs">
        {WEEK.map((d, i) => <span key={i} className="pb-1 font-bold text-slate-400">{d}</span>)}
        {cells.map((d, i) => {
          const isToday = d && `${month}-${String(d).padStart(2, "0")}` === today;
          return (
            <span key={i} className={`relative flex h-9 items-center justify-center rounded-md ${isToday ? "bg-[#1B2A4A] font-bold text-white" : d ? "text-[#1B2A4A]" : ""}`} data-testid={d ? `cal-day-${d}` : undefined}>
              {d || ""}
              {d > 0 && marked.has(d) && <i className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${isToday ? "bg-amber-400" : "bg-[#C9A84C]"}`} data-testid={`cal-dot-${d}`} />}
            </span>
          );
        })}
      </div>
    </Panel>
  );
}

export function BiggestPayments({ items }: { items: Occurrence[] }) {
  const max = Math.max(...items.map((o) => o.amount), 1);
  return (
    <Panel testId="biggest-payments">
      <PanelLabel>Los pagos más grandes</PanelLabel>
      {items.length === 0 && <p className="mt-3 text-sm text-slate-500">Sin pagos programados.</p>}
      <ul className="mt-4 space-y-3">
        {items.map((o, i) => (
          <li key={`${o.schedule_id}-${o.date}`} data-testid={`biggest-${i}`}>
            <div className="flex justify-between gap-2 text-sm"><span className="truncate text-slate-600">{o.description}</span><span className="tabular font-semibold text-[#1B2A4A]">{money(o.amount)}</span></div>
            <div className="mt-1 h-2 rounded-full bg-[#F4F5F7]"><div className="h-full rounded-full bg-[#C9A84C] transition-[width] duration-500" style={{ width: `${(o.amount / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function CardPayments({ items }: { items: CardPayment[] }) {
  return (
    <Panel testId="card-payments">
      <PanelLabel>Pagos de tarjeta</PanelLabel>
      {items.length === 0 && <p className="mt-3 text-sm text-slate-500">Agrega el día de corte a tus tarjetas para verlas aquí.</p>}
      <ul className="mt-2 divide-y divide-[#DDE1E8]">
        {items.map((c) => (
          <li key={c.account_id} className="py-3" data-testid={`card-payment-${c.account_id}`}>
            <div className="flex justify-between gap-2"><span className="font-semibold text-[#1B2A4A]">{c.name}</span><span className="tabular font-semibold text-[#B23A1E]" data-testid={`card-payment-amount-${c.account_id}`}>{money(c.remaining)}</span></div>
            <p className="mt-0.5 text-xs text-slate-500">Corte {formatDate(c.statement_date)} · Fecha límite {formatDate(c.due_date)}</p>
            <p className="text-xs text-slate-400">Para no pagar intereses: {money(c.statement_debt)} al corte{c.remaining < c.statement_debt ? `, ya abonaste ${money(c.statement_debt - c.remaining)}` : ""}</p>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
